// Ported from greenline-app (TabletPOS) PR #2837. Keep in sync until it moves to @getgreenline/homi-shared.

import { dayjs } from './dayjs'
import {
  IUberEatsDenyDraft,
  IUberEatsDenyReason,
  IUberEatsIncomingOrder,
  IUberEatsIncomingOrderEntry,
  IUberEatsItemQty,
  IUberEatsOrderItem,
  IUberEatsNewOrderPause,
  IUberEatsAlertTiming,
  IUberEatsSavedQueue,
  UberEatsDenyReasonCode,
  UberEatsNewOrderPauseOption,
  UberEatsUrgencyStage,
} from '../models/UberEats/UberEatsModels'
import { UBER_EATS_DENY_REASONS, UBER_EATS_PLACEHOLDERS } from '../constants/UberEats'

const MINUTE_MS = 60 * 1000
const JUST_NOW_MS = 60 * 1000

export const getTimeRemainingMs = (receivedAt: number, now: number, slaMs: number) =>
  Math.min(Math.max(receivedAt + slaMs - now, 0), slaMs)

export const getUrgencyStage = (
  remainingMs: number,
  timing: IUberEatsAlertTiming,
): UberEatsUrgencyStage => {
  if (remainingMs <= timing.urgentMinutes * MINUTE_MS) return 'urgent'
  if (remainingMs <= timing.warningMinutes * MINUTE_MS) return 'warning'
  return 'normal'
}

export const formatTimeRemaining = (ms: number) => {
  const totalSeconds = Math.ceil(ms / 1000)
  const seconds = totalSeconds % 60
  return `${Math.floor(totalSeconds / 60)}:${seconds < 10 ? '0' : ''}${seconds}`
}

export const getUrgencyLabel = (stage: UberEatsUrgencyStage, remainingMs: number) => {
  const label = `${formatTimeRemaining(remainingMs)} to respond`
  return stage === 'urgent' ? `${label} — Uber will auto-call the store` : label
}

// TODO: GR-14015 — client-side expiry until a server service exists
export const isExpirable = (entry: IUberEatsIncomingOrderEntry) =>
  entry.status === 'pending' || entry.status === 'failed'

export const isNewOrderPauseActive = (pause: IUberEatsNewOrderPause | null, now: number) =>
  !!pause && now < pause.until

export const isCoveredByNewOrderPause = (
  order: Pick<IUberEatsIncomingOrder, 'receivedAt'>,
  pause: IUberEatsNewOrderPause | null,
  now: number,
) => !!pause && isNewOrderPauseActive(pause, now) && order.receivedAt >= pause.startedAt

export const getNewOrderPauseUntil = (option: UberEatsNewOrderPauseOption, now: number) => {
  if (option === 'PAUSE_20_MIN') return now + 20 * MINUTE_MS
  if (option === 'PAUSE_40_MIN') return now + 40 * MINUTE_MS
  return dayjs(now).add(1, 'day').startOf('day').valueOf()
}

export const formatNewOrderPauseLabel = (pause: IUberEatsNewOrderPause | null, now: number) =>
  pause && isNewOrderPauseActive(pause, now)
    ? `Until ${dayjs(pause.until).format('h:mm A')}`
    : 'Off'

export const parseNewOrderPause = (raw: string | null): IUberEatsNewOrderPause | null => {
  if (!raw) return null

  try {
    const { startedAt, until } = JSON.parse(raw)
    return Number.isFinite(startedAt) && Number.isFinite(until) ? { startedAt, until } : null
  } catch {
    return null
  }
}

export const isDenyDraftComplete = (draft?: IUberEatsDenyDraft) => {
  if (!draft || !draft.reasonCode) return false

  const reason = UBER_EATS_DENY_REASONS.find((r) => r.code === draft.reasonCode)
  return !reason?.requiresExplanation || draft.explanation.trim() !== ''
}

export const toDenyReason = (draft: IUberEatsDenyDraft): IUberEatsDenyReason => {
  const explanation = draft.explanation.trim()
  const code = draft.reasonCode as IUberEatsDenyReason['code']
  return explanation ? { code, explanation } : { code }
}

export const formatPlacedAgo = (receivedAt: number, now: number) =>
  now - receivedAt < JUST_NOW_MS ? 'Placed just now' : `Placed ${dayjs(receivedAt).from(now)}`

export const formatOrderSummary = ({
  total,
  items,
}: Pick<IUberEatsIncomingOrder, 'total' | 'items'>) =>
  `$${total.toFixed(2)} excl. fees · ${items.length} ${items.length === 1 ? 'product' : 'products'}`

export const formatIncomingItemQty = (
  orderedQuantity: number,
  onHandQuantity: number | undefined,
): IUberEatsItemQty => {
  if (onHandQuantity === undefined) return { text: `x${orderedQuantity}`, isShortOnStock: false }

  const onHand = Math.max(0, onHandQuantity)
  return {
    text: `x${orderedQuantity} (${onHand} left in stock)`,
    isShortOnStock: onHand < orderedQuantity,
  }
}

export const formatAlertBarHeadline = ({
  displayId,
  customerName,
  items,
  total,
}: IUberEatsIncomingOrder) =>
  `#${displayId} · ${customerName} · ${items.length} ${
    items.length === 1 ? 'item' : 'items'
  } · $${total.toFixed(2)}`

export const formatBadgeCount = (count: number) => (count < 10 ? String(count) : '9+')

export const formatOrdersWaiting = (count: number) =>
  `${count} ${count === 1 ? 'order' : 'orders'} waiting`

// Below: homi-web additions for untrusted payloads, not part of the TabletPOS port.

type RawRecord = Record<string, unknown>

const isRecord = (value: unknown): value is RawRecord => typeof value === 'object' && value !== null

const textOr = (value: unknown, fallback: string) =>
  typeof value === 'string' && value.trim() !== '' ? value : fallback

const finiteOr = (value: unknown, fallback: number) =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback

const normalizeItem = (raw: unknown, index: number): IUberEatsOrderItem => {
  const item = isRecord(raw) ? raw : {}

  return {
    id: textOr(item.id, `item-${index}`),
    name: textOr(item.name, UBER_EATS_PLACEHOLDERS.itemName),
    sku: textOr(item.sku, ''),
    price: finiteOr(item.price, 0),
    quantity: finiteOr(item.quantity, 1),
    productId: textOr(item.productId, ''),
  }
}

export const isValidDenyReasonCode = (code: unknown): code is UberEatsDenyReasonCode =>
  UBER_EATS_DENY_REASONS.some((reason) => reason.code === code)

/** Returns null when the payload cannot be tracked (no id or arrival time). */
export const normalizeIncomingOrder = (raw: unknown): IUberEatsIncomingOrder | null => {
  if (!isRecord(raw)) return null
  if (typeof raw.id !== 'string' || raw.id === '') return null
  if (typeof raw.receivedAt !== 'number' || !Number.isFinite(raw.receivedAt)) return null

  const items = Array.isArray(raw.items) ? raw.items.map(normalizeItem) : []
  const itemsTotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0)

  return {
    id: raw.id,
    displayId: textOr(raw.displayId, UBER_EATS_PLACEHOLDERS.displayId),
    customerName: textOr(raw.customerName, UBER_EATS_PLACEHOLDERS.customerName),
    items,
    total: finiteOr(raw.total, itemsTotal),
    receivedAt: raw.receivedAt,
  }
}

const toSavedEntry = (entry: unknown): IUberEatsIncomingOrderEntry[] => {
  const order = isRecord(entry) ? normalizeIncomingOrder(entry.order) : null
  if (!isRecord(entry) || !order) return []
  if (entry.status === 'expired') return [{ order, status: 'expired' }]
  if (entry.status !== 'failed') return [{ order, status: 'pending' }]

  return [
    {
      order,
      status: 'failed',
      failedAction: entry.failedAction === 'deny' ? 'deny' : 'accept',
      errorMessage: textOr(entry.errorMessage, 'Request to Uber Eats failed'),
    },
  ]
}

export const EMPTY_SAVED_QUEUE: IUberEatsSavedQueue = { entries: [], decidedOrderIds: [] }

/**
 * Restores the queue saved before a page reload (shift start/end reloads the POS).
 * A queue saved for another company, location or device is discarded, so a different login on the
 * same browser never sees it. A request that was in flight has an unknown outcome, so its order
 * comes back as pending.
 */
export const parseSavedIncomingQueue = (raw: string | null, scope: string): IUberEatsSavedQueue => {
  if (!raw) return EMPTY_SAVED_QUEUE

  let saved: unknown
  try {
    saved = JSON.parse(raw)
  } catch {
    return EMPTY_SAVED_QUEUE
  }
  if (!isRecord(saved) || saved.scope !== scope) return EMPTY_SAVED_QUEUE

  return {
    entries: Array.isArray(saved.entries) ? saved.entries.flatMap(toSavedEntry) : [],
    decidedOrderIds: Array.isArray(saved.decidedOrderIds)
      ? saved.decidedOrderIds.filter((id): id is string => typeof id === 'string')
      : [],
  }
}
